/**
 * Tasks talk back (#1645) — the progress line. Since Tasks Both Ways slice 3
 * (10/28) it reads in the six stage words (lib/task-stages.ts):
 * New → Seen → Doing → Done. A handed-back or closed task is Done; a
 * can't-do stops at Done's spot in red, and its label reads "Can't do". One component for the Sent rows (compact) and the drawer
 * (with labels). Colours carry meaning twice over: the tone also changes the
 * reached dots' fill, and the current step's label is bold, so it never rests
 * on hue alone.
 */
import { cn } from "@/lib/utils";
import type { TaskStage } from "@/lib/tasks";
import { STAGE_WORDS } from "@/lib/task-stages";

export const STAGE_STEPS: Array<{ stage: TaskStage; label: string }> = [
    { stage: "sent", label: STAGE_WORDS.new },
    { stage: "seen", label: STAGE_WORDS.seen },
    { stage: "working", label: STAGE_WORDS.doing },
    { stage: "handed_back", label: STAGE_WORDS.done },
];

export type RailTone = "normal" | "done" | "bad" | "passed" | "waiting";

const TONE_FILL: Record<RailTone, string> = {
    normal: "bg-primary border-primary",
    done: "bg-emerald-700 border-emerald-700",
    bad: "bg-red-700 border-red-700",
    passed: "bg-orange-700 border-orange-700",
    waiting: "bg-violet-700 border-violet-700",
};

const TONE_LINE: Record<RailTone, string> = {
    normal: "bg-primary",
    done: "bg-emerald-700",
    bad: "bg-red-700",
    passed: "bg-orange-700",
    waiting: "bg-violet-700",
};

const TONE_TEXT: Record<RailTone, string> = {
    normal: "text-primary",
    done: "text-emerald-700 dark:text-emerald-400",
    bad: "text-red-700 dark:text-red-400",
    passed: "text-orange-700 dark:text-orange-400",
    waiting: "text-violet-700 dark:text-violet-400",
};

export function stageIndex(stage: TaskStage): number {
    if (stage === "canceled" || stage === "confirmed" || stage === "cant_do") return 3;
    return Math.max(
        0,
        STAGE_STEPS.findIndex((s) => s.stage === stage),
    );
}

export function TaskProgressRail({
    stage,
    tone = "normal",
    showLabels = false,
    className,
}: {
    stage: TaskStage;
    tone?: RailTone;
    showLabels?: boolean;
    className?: string;
}) {
    const at = stageIndex(stage);
    const big = showLabels;
    return (
        <div
            className={cn("w-full", className)}
            role="img"
            aria-label={`Progress: ${stage === "canceled" ? "Canceled" : stage === "cant_do" ? STAGE_WORDS.cant_do : STAGE_STEPS[at].label}`}
        >
            <div className="flex w-full items-center">
                {STAGE_STEPS.map((s, i) => {
                    const reached = i <= at;
                    const current = i === at;
                    return (
                        <div key={s.stage} className={cn("flex items-center", i < STAGE_STEPS.length - 1 && "flex-1")}>
                            <span
                                className={cn(
                                    "shrink-0 rounded-full border-2",
                                    big ? (current ? "h-4 w-4" : "h-3.5 w-3.5") : current ? "h-3 w-3" : "h-2.5 w-2.5",
                                    reached ? TONE_FILL[tone] : "border-border bg-card",
                                    current && big && "ring-4 ring-primary/15",
                                )}
                            />
                            {i < STAGE_STEPS.length - 1 && (
                                <span
                                    className={cn(
                                        "mx-0.5 flex-1 rounded-full",
                                        big ? "h-[3px]" : "h-0.5",
                                        i < at ? TONE_LINE[tone] : "bg-border",
                                    )}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            {showLabels && (
                <div className="mt-1.5 flex justify-between">
                    {STAGE_STEPS.map((s, i) => (
                        <span
                            key={s.stage}
                            className={cn(
                                "text-[11px]",
                                i === at ? cn("font-bold", TONE_TEXT[tone]) : "font-medium",
                                i <= at ? (i === at ? "" : "text-foreground/80") : "text-muted-foreground",
                            )}
                        >
                            {i === at && stage === "cant_do" ? STAGE_WORDS.cant_do : s.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
